// Тексты кабинета репетитора /{lang}/tutors/me (src/lib/partner-cabinet.ts).
// В AZ и EN нет кириллицы — проверяет scripts/verify-tutor-cabinet.mts.
// AZ — на вычитку.
import type { SiteLang } from "@/components/site/SiteChrome";

export const TUTORS_ME_PATH: Record<SiteLang, string> = {
  ru: "/ru/tutors/me",
  az: "/az/tutors/me",
  en: "/en/tutors/me",
};

const SUPPORT = "support@yolutap.com";

/** Подписи модерируемых полей — список «ждут проверки» в кабинете. */
type FieldLabels = Record<
  | "name"
  | "address_mode"
  | "point"
  | "city"
  | "district"
  | "metro"
  | "travel_districts"
  | "phone"
  | "instagram"
  | "about"
  | "photo",
  string
>;

export type CabinetCopy = {
  metaTitle: string;
  title: string;
  /** Экран после отправки анкеты на /tutors/join. */
  afterJoin: string;
  afterJoinLink: string;
  /** Правка: сохранённое место строкой и кнопка, которая открывает карту. */
  placeSaved: string;
  placeChange: string;
  /** Профиль /tutors/<slug>: ссылка в кабинет и пометка паузы. */
  profileLink: string;
  profilePaused: string;
  profilePausedUntil: (date: string) => string;
  // вход
  loginLead: string;
  email: string;
  send: string;
  sending: string;
  requestSent: string;
  errEmail: string;
  errGeneric: string;
  // кабинет
  loading: string;
  loadError: string;
  hello: (name: string) => string;
  status: {
    published: string;
    pending: string;
    paused: string;
    pausedUntil: (date: string) => string;
  };
  openProfile: string;
  logout: string;
  tasksTitle: string;
  taskGo: string;
  taskLater: string;
  pendingTitle: string;
  pendingText: (date: string) => string;
  fields: FieldLabels;
  formTitle: string;
  formLead: string;
  emailLocked: (email: string) => string;
  kindNote: string;
  photoNote: string;
  save: string;
  saving: string;
  savedNow: string;
  savedModeration: string;
  savedDirect: string;
  savedNothing: string;
  // пауза
  pauseTitle: string;
  pauseLead: string;
  pauseFull: string;
  pauseVacation: string;
  pauseUntil: string;
  pauseSave: string;
  pauseOff: string;
  pauseNow: (until: string | null) => string;
  errPauseUntil: string;
  // удаление
  deleteTitle: string;
  deleteLead: string;
  deleteConfirm: string;
  deleteButton: string;
  deleted: string;
};

export const CABINET_COPY: Record<SiteLang, CabinetCopy> = {
  ru: {
    metaTitle: "Кабинет репетитора — Yolutap",
    title: "Кабинет",
    afterJoin: "Анкету можно будет менять самим, без писем нам:",
    afterJoinLink: "кабинет репетитора",
    placeSaved: "Сохранённый адрес",
    placeChange: "Изменить",
    profileLink: "Это ваш профиль? Войти в кабинет",
    profilePaused: "Сейчас не набирает учеников",
    profilePausedUntil: (d) => `Не набирает учеников до ${d}`,
    loginLead: "Укажите почту из анкеты: пришлём на неё ссылку для входа. Пароль не нужен.",
    email: "Почта",
    send: "Прислать ссылку",
    sending: "Отправляем…",
    requestSent:
      "Если анкета с такой почтой есть, ссылка для входа придёт в течение нескольких минут. Она действует 24 часа.",
    errEmail: "Проверьте почту: похоже, в ней опечатка.",
    errGeneric: "Не получилось. Попробуйте ещё раз.",
    loading: "Загружаем кабинет…",
    loadError: "Не получилось загрузить кабинет. Обновите страницу.",
    hello: (n) => `Здравствуйте, ${n}!`,
    status: {
      published: "Анкета на карте",
      pending: "Анкета на проверке — скоро появится на карте",
      paused: "Анкета на паузе: её нет в списке и на карте, по прямой ссылке открывается",
      pausedUntil: (d) => `Анкета на паузе до ${d}`,
    },
    openProfile: "Открыть мою анкету",
    logout: "Выйти",
    tasksTitle: "Нужно ваше действие",
    taskGo: "Заполнить",
    taskLater: "Скрыть",
    pendingTitle: "Изменения на проверке",
    pendingText: (d) =>
      `Отправлены ${d}. Пока мы их проверяем, родители видят прежнюю анкету. Эти поля ждут проверки:`,
    fields: {
      name: "имя",
      address_mode: "показ адреса",
      point: "пин на карте",
      city: "город",
      district: "район",
      metro: "метро",
      travel_districts: "районы выезда",
      phone: "телефон / WhatsApp",
      instagram: "Instagram",
      about: "описание",
      photo: "фото",
    },
    formTitle: "Анкета",
    formLead:
      "Цена, предметы, классы и формат занятий меняются сразу. Имя, фото, описание, адрес, телефон и Instagram — после нашей проверки.",
    emailLocked: (e) => `Почта для входа: ${e}. Чтобы сменить её, напишите на ${SUPPORT}.`,
    kindNote: `Вид анкеты (репетитор или кружок) здесь не меняется. Если нужно, напишите на ${SUPPORT}.`,
    photoNote: "Новое фото появится на карте после проверки.",
    save: "Сохранить",
    saving: "Сохраняем…",
    savedNow: "Сохранено, уже на карте.",
    savedModeration: "Остальное отправлено на проверку: на карте появится после неё.",
    savedDirect: "Сохранено. Анкета ещё на проверке — посмотрим её целиком перед публикацией.",
    savedNothing: "Изменений нет.",
    pauseTitle: "Пауза",
    pauseLead:
      "Если сейчас не берёте учеников: анкета пропадёт из списка и с карты, но откроется по прямой ссылке. Снять паузу — одной кнопкой.",
    pauseFull: "Набор закрыт",
    pauseVacation: "В отпуске до…",
    pauseUntil: "До какого числа",
    pauseSave: "Поставить на паузу",
    pauseOff: "Снять паузу",
    pauseNow: (u) => (u ? `Сейчас на паузе до ${u}.` : "Сейчас на паузе: набор закрыт."),
    errPauseUntil: "Выберите дату не раньше сегодняшней и не дальше чем через год.",
    deleteTitle: "Удалить анкету",
    deleteLead: "Анкета пропадёт с карты и по ссылке. Вернуть её можно, написав нам.",
    deleteConfirm: "Да, удалить мою анкету",
    deleteButton: "Удалить анкету",
    deleted: "Анкета удалена. Если передумаете, напишите на support@yolutap.com.",
  },
  az: {
    metaTitle: "Repetitor kabineti — Yolutap",
    title: "Kabinet",
    afterJoin: "Anketi bizə yazmadan özünüz dəyişə biləcəksiniz:",
    afterJoinLink: "repetitor kabineti",
    placeSaved: "Yadda saxlanmış ünvan",
    placeChange: "Dəyiş",
    profileLink: "Bu sizin profilinizdir? Kabinetə daxil olun",
    profilePaused: "Hazırda şagird qəbul etmir",
    profilePausedUntil: (d) => `${d} tarixinədək şagird qəbul etmir`,
    loginLead: "Anketdəki e-poçtu yazın: ona giriş linki göndərəcəyik. Parol lazım deyil.",
    email: "E-poçt",
    send: "Linki göndərin",
    sending: "Göndərilir…",
    requestSent:
      "Bu e-poçtla anket varsa, giriş linki bir neçə dəqiqəyə gələcək. Link 24 saat etibarlıdır.",
    errEmail: "E-poçtu yoxlayın: deyəsən, səhv var.",
    errGeneric: "Alınmadı. Yenidən cəhd edin.",
    loading: "Kabinet yüklənir…",
    loadError: "Kabineti yükləmək alınmadı. Səhifəni yeniləyin.",
    hello: (n) => `Salam, ${n}!`,
    status: {
      published: "Anket xəritədədir",
      pending: "Anket yoxlanılır — tezliklə xəritədə görünəcək",
      paused: "Anket fasilədədir: siyahıda və xəritədə görünmür, birbaşa linklə açılır",
      pausedUntil: (d) => `Anket ${d} tarixinədək fasilədədir`,
    },
    openProfile: "Anketimi açın",
    logout: "Çıxış",
    tasksTitle: "Sizdən addım gözlənilir",
    taskGo: "Doldurun",
    taskLater: "Gizlət",
    pendingTitle: "Dəyişikliklər yoxlanılır",
    pendingText: (d) =>
      `${d} göndərilib. Yoxlayana qədər valideynlər əvvəlki anketi görür. Bu sahələr yoxlama gözləyir:`,
    fields: {
      name: "ad",
      address_mode: "ünvanın göstərilməsi",
      point: "xəritədə pin",
      city: "şəhər",
      district: "rayon",
      metro: "metro",
      travel_districts: "gediş rayonları",
      phone: "telefon / WhatsApp",
      instagram: "Instagram",
      about: "təsvir",
      photo: "şəkil",
    },
    formTitle: "Anket",
    formLead:
      "Qiymət, fənlər, siniflər və dərs formatı dərhal dəyişir. Ad, şəkil, təsvir, ünvan, telefon və Instagram — yoxlamamızdan sonra.",
    emailLocked: (e) => `Giriş üçün e-poçt: ${e}. Dəyişmək üçün ${SUPPORT} ünvanına yazın.`,
    kindNote: `Anketin növü (repetitor və ya dərnək) burada dəyişmir. Lazımdırsa, ${SUPPORT} ünvanına yazın.`,
    photoNote: "Yeni şəkil yoxlamadan sonra xəritədə görünəcək.",
    save: "Yadda saxla",
    saving: "Yadda saxlanılır…",
    savedNow: "Yadda saxlandı, artıq xəritədədir.",
    savedModeration: "Qalanı yoxlamaya göndərildi: ondan sonra xəritədə görünəcək.",
    savedDirect:
      "Yadda saxlandı. Anket hələ yoxlanılır — dərc etməzdən əvvəl onu tam yoxlayacağıq.",
    savedNothing: "Dəyişiklik yoxdur.",
    pauseTitle: "Fasilə",
    pauseLead:
      "Hazırda şagird götürmürsünüzsə: anket siyahıdan və xəritədən çıxacaq, amma birbaşa linklə açılacaq. Fasiləni bir düymə ilə götürə bilərsiniz.",
    pauseFull: "Qəbul bağlıdır",
    pauseVacation: "Məzuniyyətdəyəm…",
    pauseUntil: "Hansı tarixədək",
    pauseSave: "Fasiləyə qoy",
    pauseOff: "Fasiləni götür",
    pauseNow: (u) =>
      u ? `Hazırda ${u} tarixinədək fasilədədir.` : "Hazırda fasilədədir: qəbul bağlıdır.",
    errPauseUntil: "Bu gündən tez və bir ildən gec olmayan tarix seçin.",
    deleteTitle: "Anketi silin",
    deleteLead: "Anket xəritədən və linkdən yox olacaq. Bizə yazaraq onu bərpa etmək olar.",
    deleteConfirm: "Bəli, anketimi silin",
    deleteButton: "Anketi silin",
    deleted: "Anket silindi. Fikrinizi dəyişsəniz, support@yolutap.com ünvanına yazın.",
  },
  en: {
    metaTitle: "Tutor account — Yolutap",
    title: "Your account",
    afterJoin: "You will be able to edit your profile yourself, without writing to us:",
    afterJoinLink: "your tutor account",
    placeSaved: "Saved address",
    placeChange: "Change",
    profileLink: "Is this your profile? Sign in",
    profilePaused: "Not taking students right now",
    profilePausedUntil: (d) => `Not taking students until ${d}`,
    loginLead:
      "Enter the email from your profile and we'll send you a sign-in link. No password needed.",
    email: "Email",
    send: "Send the link",
    sending: "Sending…",
    requestSent:
      "If there's a profile with this email, the sign-in link will arrive in a few minutes. It works for 24 hours.",
    errEmail: "Check the email: it looks like there's a typo.",
    errGeneric: "Something went wrong. Please try again.",
    loading: "Loading your account…",
    loadError: "Couldn't load your account. Please refresh the page.",
    hello: (n) => `Hi ${n}!`,
    status: {
      published: "Your profile is on the map",
      pending: "Your profile is under review — it'll be on the map soon",
      paused:
        "Your profile is paused: it's not in the list or on the map, but opens by direct link",
      pausedUntil: (d) => `Your profile is paused until ${d}`,
    },
    openProfile: "Open my profile",
    logout: "Sign out",
    tasksTitle: "Action needed",
    taskGo: "Fill in",
    taskLater: "Hide",
    pendingTitle: "Changes under review",
    pendingText: (d) =>
      `Sent on ${d}. Until we review them, parents see your current profile. These fields are waiting for review:`,
    fields: {
      name: "name",
      address_mode: "how the address is shown",
      point: "map pin",
      city: "city",
      district: "district",
      metro: "metro",
      travel_districts: "districts you travel to",
      phone: "phone / WhatsApp",
      instagram: "Instagram",
      about: "description",
      photo: "photo",
    },
    formTitle: "Profile",
    formLead:
      "Price, subjects, grades and lesson format change right away. Name, photo, description, address, phone and Instagram change after our review.",
    emailLocked: (e) => `Sign-in email: ${e}. To change it, write to ${SUPPORT}.`,
    kindNote: `The profile type (tutor or club) can't be changed here. If you need to, write to ${SUPPORT}.`,
    photoNote: "A new photo goes live after review.",
    save: "Save",
    saving: "Saving…",
    savedNow: "Saved, already on the map.",
    savedModeration: "The rest is sent for review and will go live after it.",
    savedDirect:
      "Saved. Your profile is still under review — we'll check all of it before it goes live.",
    savedNothing: "Nothing changed.",
    pauseTitle: "Pause",
    pauseLead:
      "Not taking students right now? Your profile leaves the list and the map but still opens by direct link. One button turns the pause off.",
    pauseFull: "Not taking students",
    pauseVacation: "On holiday until…",
    pauseUntil: "Until",
    pauseSave: "Pause my profile",
    pauseOff: "Turn pause off",
    pauseNow: (u) => (u ? `Paused until ${u}.` : "Paused: not taking students."),
    errPauseUntil: "Pick a date from today up to a year ahead.",
    deleteTitle: "Delete profile",
    deleteLead:
      "Your profile disappears from the map and its link. You can get it back by writing to us.",
    deleteConfirm: "Yes, delete my profile",
    deleteButton: "Delete profile",
    deleted: "Your profile is deleted. If you change your mind, write to support@yolutap.com.",
  },
};
